/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
	PreviewImage,
	PreviewPopoverArrow,
	PreviewPopoverContainer,
} from '../styles/previewPopover.styled';

const SHOW_PREVIEW_POPOVER_AFTER_MS = 100;
export const TRANSPARENT_EXTENSIONS = ['png', 'eps', 'ai'];

const isTransparent = (extensions: string[]) => {
	return TRANSPARENT_EXTENSIONS.some((extension) => extensions.includes(extension));
};

type Size = {
	height: number;
	width: number;
};

interface Rect extends Size {
	left: number;
	top: number;
}

interface Position {
	left: number;
	top: number;
}

type Props = {
	imageSrc: string;
	fallbackImageSrc?: string;
	imageExtensions: string[];
};

export enum DisplayPreviewPopover {
	Show = 'flex',
	Hide = 'none',
}

const PreviewPopover = React.forwardRef<HTMLElement, Props>((props, parentRef) => {
	const [isVisible, setIsVisible] = useState(false);
	const [popoverRect, setPopoverRect] = useState<Position>();
	const [displayPopover, setDisplayPopover] = useState<DisplayPreviewPopover>(
		DisplayPreviewPopover.Hide,
	);
	const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });
	const [mousePositionInsideElement, setMousePositionInsideElement] = useState(0);

	const onMouseOver = useDebouncedCallback((e) => {
		const elementBounds = e.target.getBoundingClientRect();
		setMousePositionInsideElement(e.pageX - elementBounds.left);

		// Prevents Popover from flickering when cursor is too near the edge of the element
		if (mousePositionInsideElement <= 7) {
			return;
		}

		setIsVisible(true);
	}, SHOW_PREVIEW_POPOVER_AFTER_MS);

	useEffect(() => {
		const node = (parentRef as React.RefObject<HTMLElement>).current;

		const onMouseOut = () => {
			setIsVisible(false);
		};

		if (!node) {
			return;
		}

		node.addEventListener('mousemove', onMouseOver);
		node.addEventListener('mouseleave', onMouseOver.cancel);
		node.addEventListener('mouseleave', onMouseOut);

		return () => {
			node.removeEventListener('mousemove', onMouseOver);
			node.removeEventListener('mouseleave', onMouseOut);
			node.addEventListener('mouseleave', onMouseOut);
		};
	}, [parentRef, onMouseOver, mousePositionInsideElement]);

	const onImageLoaded = (image: HTMLImageElement) => {
		setImageSize({ width: image.offsetWidth, height: image.offsetHeight });
		setDisplayPopover(DisplayPreviewPopover.Show);
	};

	const onImageLoadError = (image: HTMLImageElement) => {
		if (props.fallbackImageSrc && image.src !== props.fallbackImageSrc) {
			image.src = props.fallbackImageSrc;
		}
	};

	useEffect(() => {
		if (imageSize === null) {
			return;
		}

		const assetElement = (parentRef as React.RefObject<HTMLElement>).current!;
		const assetItemRect = getElementRect(assetElement);

		setPopoverRect(getPopoverPosition(imageSize, assetItemRect));
	}, [parentRef, imageSize]);

	const hasTransparentBackground = isTransparent(props.imageExtensions);

	return isVisible ? (
		<PreviewPopoverContainer
			style={{ ...popoverRect, display: displayPopover }}
			hasTransparentBackground={hasTransparentBackground}
		>
			<PreviewPopoverArrow />
			<PreviewImage
				alt=""
				src={props.imageSrc}
				onLoad={(event) => onImageLoaded(event.currentTarget)}
				onError={(event) => onImageLoadError(event.currentTarget)}
			/>
		</PreviewPopoverContainer>
	) : null;
});

const getPopoverPosition = (imageSize: Size, elementOffsets: Rect) => {
	return {
		top: elementOffsets.top - elementOffsets.height - elementOffsets.height / 2,
		left: -(elementOffsets.width / 2 + 10),
	};
};

const getElementRect = (element: HTMLElement) => {
	return {
		left: element.offsetLeft,
		width: element.offsetWidth,
		height: element.offsetHeight,
		top: element.offsetTop,
	};
};

export default PreviewPopover;
